/* Rieseguire una configurazione già salvata per ottenerne le serie.
 *
 * Perché serve. I backtest nati da una griglia — cioè quasi tutti: oltre un
 * milione e mezzo — hanno le metriche ma NON hanno `trades`,
 * `equityLinePoints` e `candlesClosePrices`. Non è una dimenticanza: il comando
 * di gruppo li toglie apposta prima di salvare, perché sono megabyte a riga e
 * su una griglia da trentaseimila righe sarebbero terabyte. Il risultato
 * pratico è che la configurazione migliore trovata in una ricerca si apre senza
 * curva del capitale e senza operazioni, che sono le due cose che si guardano
 * prima di metterci dei soldi.
 *
 * L'unico modo di averle è rifare il calcolo per quella riga sola, e per farlo
 * serve `force`: senza, il server riconosce che una riga identica esiste già e
 * restituisce quella — cioè proprio la riga vuota da cui si è partiti.
 *
 * Le unità NON sono simmetriche, e sbagliarle significa misurare costi diversi
 * da quelli di partenza:
 *  - le commissioni si INVIANO in percentuale e il server le divide per cento
 *    prima di scriverle in `traderParams`, quindi rileggendole vanno
 *    rimoltiplicate;
 *  - lo slippage si invia e si salva nella stessa unità (percentuale), perché è
 *    il trader a dividerlo per cento al momento dell'uso.
 */

import type { AvvioBacktest, Backtest } from '../../api/tipi';

/** I costi come li vuole il modulo: tutti in percentuale, tutti valorizzati. */
export interface CostiEsecuzione {
  feeMode: 'MAKER' | 'TAKER';
  makerFee: number;
  takerFee: number;
  slippage: number;
}

/** Nessun costo dichiarato: è ciò che le griglie usano davvero. */
export const COSTI_GRIGLIA: CostiEsecuzione = {
  feeMode: 'TAKER',
  makerFee: 0,
  takerFee: 0,
  slippage: 0,
};

function numero(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * I costi con cui una riga è stata misurata, riportati nelle unità del modulo.
 *
 * `null` quando la riga non li porta con sé — è il caso delle griglie, che
 * salvano i backtest senza `traderParams`. Restituire zeri in quel caso sarebbe
 * un dato inventato: chi legge deve poter distinguere «misurato senza costi» da
 * «non sappiamo con quali costi».
 */
export function costiDaTraderParams(
  traderParams: Record<string, unknown> | null | undefined,
): CostiEsecuzione | null {
  if (!traderParams) return null;
  const maker = numero(traderParams.makerFee);
  const taker = numero(traderParams.takerFee);
  const slip = numero(traderParams.slippage);
  const modo = traderParams.feeMode === 'MAKER' ? 'MAKER' : 'TAKER';
  if (maker == null && taker == null && slip == null) return null;
  return {
    feeMode: modo,
    makerFee: (maker ?? 0) * 100,
    takerFee: (taker ?? 0) * 100,
    slippage: slip ?? 0,
  };
}

/** Capitale iniziale della riga, con lo stesso ripiego del comando di gruppo. */
export function capitaleIniziale(b: Backtest): { assetBalance: number; currencyBalance: number } {
  const asset = numero(b.traderParams?.assetBalance) ?? numero(b.initialAsset) ?? 0;
  const currency = numero(b.traderParams?.currencyBalance) ?? numero(b.initialCurrency) ?? 1000;
  return { assetBalance: asset, currencyBalance: currency };
}

/**
 * Il corpo di `POST /backtester/run` per rifare esattamente questa riga.
 *
 * `force: true` è obbligatorio: senza, il riuso lato server restituirebbe la
 * riga di partenza, che è vuota. Le date si mandano come le ha il server,
 * perché il confronto con le candele avviene sulle stesse stringhe ISO.
 */
export function payloadRiesecuzione(b: Backtest, costi?: CostiEsecuzione): AvvioBacktest {
  const { assetBalance, currencyBalance } = capitaleIniziale(b);
  const c = costi ?? costiDaTraderParams(b.traderParams) ?? COSTI_GRIGLIA;
  return {
    exchangeId: b.exchangeId,
    asset: b.asset,
    currency: b.currency,
    startDate: b.startDate,
    endDate: b.endDate,
    strategyName: b.strategyName,
    strategyParams: b.strategyParams ?? {},
    warmupPeriod: b.warmupPeriod,
    candleSize: b.candleSize,
    assetBalance,
    currencyBalance,
    feeMode: c.feeMode,
    makerFee: c.makerFee,
    takerFee: c.takerFee,
    slippage: c.slippage,
    force: true,
  };
}

/**
 * Gli stessi parametri, ma come query string per il modulo del backtest libero.
 *
 * Serve al percorso «rifai cambiando una cosa»: dalla riga si arriva al modulo
 * già compilato, e si cambia solo il campo che interessa. Ricopiare a mano
 * quattordici campi è il modo migliore per sbagliarne uno senza accorgersene —
 * e nei backtest un campo sbagliato non dà errore, dà un altro risultato.
 */
export function parametriPerModulo(b: Backtest): string {
  const p = new URLSearchParams();
  p.set('exchangeId', String(b.exchangeId));
  p.set('asset', b.asset);
  p.set('currency', b.currency);
  p.set('startDate', b.startDate);
  p.set('endDate', b.endDate);
  p.set('strategyName', b.strategyName);
  p.set('candleSize', String(b.candleSize));
  p.set('warmupPeriod', String(b.warmupPeriod));
  /* I parametri di strategia come JSON: sono un oggetto di forma diversa per
     ognuna delle 65 strategie, e appiattirli in chiavi separate obbligherebbe
     il modulo a indovinare quali chiavi sono parametri e quali no. */
  if (b.strategyParams) p.set('params', JSON.stringify(b.strategyParams));

  const { assetBalance, currencyBalance } = capitaleIniziale(b);
  p.set('assetBalance', String(assetBalance));
  p.set('currencyBalance', String(currencyBalance));

  const costi = costiDaTraderParams(b.traderParams);
  if (costi) {
    p.set('feeMode', costi.feeMode);
    p.set('makerFee', String(costi.makerFee));
    p.set('takerFee', String(costi.takerFee));
    p.set('slippage', String(costi.slippage));
  }
  return p.toString();
}
